import { Stack } from '@mui/material';
import { RefObject, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';

import { SectionHeaderWithAccent } from '@/components/display/SectionHeaderWithAccent/SectionHeaderWithAccent';
import { Button } from '@/components/form/baseInputs/Button';
import { EyeIcon } from '@/components/icons/EyeIcon';
import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { useModalState } from '@/hooks/useModalState';
import { useDispositiveProvisionSuggestionsContext } from '@/modules/aiDP/context/dispositiveProvisionSuggestions.context';
import { GeneratingIndicator } from '@/modules/aiSuggestions/components/GeneratingIndicator';
import { useSuggestionAcknowledgementContext } from '@/modules/aiSuggestions/context/SuggestionAcknowledgement.context';
import { DispositiveProvisionsFormVariant } from '@/modules/dispositiveProvisions/contexts/dispositiveProvisions.context';
import { DeathOrderSelector } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/header/DeathOrderSelector';
import { EntityPreviewModal } from '@/modules/entities/components/EntityPreviewModal/EntityPreviewModal';
import { useEntityDetailsContext } from '@/modules/entities/contexts/entityDetails/entityDetails.context';
import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { EditEntitySplitScreenFormSectionSwitcher } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreenFormSectionSwitcher';
import { EditTestamentaryEntitySection } from '@/modules/entities/testamentaryEntities/EditTestamentaryEntitySplitScreenModal/EditTestamentaryEntitySplitScreen.types';
import { EditTestamentaryEntitySplitScreenFormSectionSwitcher } from '@/modules/entities/testamentaryEntities/EditTestamentaryEntitySplitScreenModal/EditTestamentaryEntitySplitScreenFormSectionSwitcher';
import { useEstateSuggestionGenerationState } from '@/modules/estate/hooks/useEstateSuggestionGenerationState';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { AiSuggestionKind, WorkflowTrackerKind } from '@/types/schema';

interface DispositiveProvisionsFormV2ModalHeaderProps {
  formVariant: DispositiveProvisionsFormVariant;
  headerRef: RefObject<HTMLDivElement | null>;
  headerText: string;
  suggestionsTargetId: string;
  workflowTrackerTargetId: string | null;
  handleEditEntitySplitScreenOnSubmit: (
    afterSubmitSection?: EditEntitySection
  ) => void;
  /** The ID of the primary client who is currently selected to die first */
  selectedFirstDeathClientId: string;
  /** Handler for section switching when variant is 'editTestamentaryEntity'. Saves the form before navigating. */
  handleTestamentaryEntitySplitScreenOnSubmit?: (
    afterSubmitSection?: EditTestamentaryEntitySection
  ) => void;
}

export function DispositiveProvisionsFormV2ModalHeader({
  headerRef,
  formVariant,
  headerText,
  suggestionsTargetId,
  workflowTrackerTargetId,
  handleEditEntitySplitScreenOnSubmit,
  selectedFirstDeathClientId,
  handleTestamentaryEntitySplitScreenOnSubmit,
}: DispositiveProvisionsFormV2ModalHeaderProps) {
  const { isTwoClientHousehold, primaryClients, householdId } =
    useHouseholdDetailsContext();
  const { entityId } = useEntityDetailsContext();
  const [
    { isModalOpen: isPreviewModalOpen },
    { openModal: openPreviewModal, closeModal: closePreviewModal },
  ] = useModalState();
  const { suggestionsByKind, refetchSuggestions } =
    useDispositiveProvisionSuggestionsContext();
  const { acknowledgedSuggestions } = useSuggestionAcknowledgementContext();
  const useKGV2DPSuggestions = useFeatureFlag('aikg_v2_associations_poc');
  const handleDPSuggestionsGenerationCompleted = useCallback(() => {
    refetchSuggestions(suggestionsTargetId);
  }, [refetchSuggestions, suggestionsTargetId]);
  const isGeneratingDPSuggestions = useEstateSuggestionGenerationState({
    entityId: workflowTrackerTargetId,
    workflowTrackerKind: WorkflowTrackerKind.AiSuggestionsV2DpGeneration,
    onCompleted: handleDPSuggestionsGenerationCompleted,
  });

  const handleDeathOrderChange = useCallback(
    (newFirstDeathClientId: string) => {
      handleEditEntitySplitScreenOnSubmit(
        newFirstDeathClientId === (primaryClients?.[0]?.id ?? '')
          ? EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_1
          : EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_2
      );
    },
    [handleEditEntitySplitScreenOnSubmit, primaryClients]
  );

  // Calculate unacknowledged suggestion counts for each death order
  // Uses the same logic as useOppositeDeathOrderSuggestions hook for consistency
  const suggestionCountByClientId = useMemo(() => {
    if (!primaryClients) return {};

    const counts: Record<string, number> = {};

    primaryClients.forEach((client, idx) => {
      if (useKGV2DPSuggestions) {
        const suggestions = suggestionsByKind[AiSuggestionKind.DpRecipientV2];
        counts[client.id] =
          suggestions?.filter(
            (s) =>
              s.dpRecipient &&
              !acknowledgedSuggestions[s.id] &&
              s.dpFirstDeathGrantorClientProfile?.id === client.id
          ).length ?? 0;
        return;
      }

      const legacySuggestionKind =
        idx === 0
          ? AiSuggestionKind.DpRecipientGrantor_1DiesFirst
          : AiSuggestionKind.DpRecipientGrantor_2DiesFirst;
      const legacySuggestions = suggestionsByKind[legacySuggestionKind] ?? [];
      // Count only unacknowledged suggestions (no acceptanceStatus)
      counts[client.id] = legacySuggestions.filter(
        (s) => s.dpRecipient && !acknowledgedSuggestions[s.id]
      ).length;
    });

    return counts;
  }, [
    acknowledgedSuggestions,
    primaryClients,
    suggestionsByKind,
    useKGV2DPSuggestions,
  ]);

  const renderBadge = useCallback(
    ({ clientId, isSelected }: { clientId: string; isSelected: boolean }) => {
      // Don't show badge for selected item - user can see suggestions in current view
      if (isSelected) return null;
      const count = suggestionCountByClientId[clientId] ?? 0;
      if (count === 0) return null;
      return <Badge variant={BadgeVariants.Success} display={String(count)} />;
    },
    [suggestionCountByClientId]
  );

  const renderSectionSwitcher = () => {
    if (formVariant === 'editEntity') {
      return (
        <EditEntitySplitScreenFormSectionSwitcher
          onSubmit={handleEditEntitySplitScreenOnSubmit}
        />
      );
    }

    if (
      formVariant === 'editTestamentaryEntity' &&
      handleTestamentaryEntitySplitScreenOnSubmit
    ) {
      return (
        <EditTestamentaryEntitySplitScreenFormSectionSwitcher
          onSubmit={handleTestamentaryEntitySplitScreenOnSubmit}
        />
      );
    }

    return null;
  };

  const headerControls = (
    <Stack direction="row" alignItems="center" spacing={1}>
      {isGeneratingDPSuggestions && (
        <GeneratingIndicator dataTestId="dispositive-provisions-form" />
      )}
      {formVariant === 'editEntity' && entityId && householdId && (
        <Button
          variant="secondary"
          size="sm"
          startIcon={EyeIcon}
          onClick={() => openPreviewModal()}
        >
          Preview deliverable
        </Button>
      )}
      {isTwoClientHousehold && (
        <DeathOrderSelector
          selectedFirstDeathClientId={selectedFirstDeathClientId}
          onDeathOrderChange={handleDeathOrderChange}
          renderBadge={renderBadge}
        />
      )}
      {renderSectionSwitcher()}
    </Stack>
  );

  // In the edit-entity split screen the stable header shell renders the title
  // and provides this slot, so portal only the controls — that keeps the title
  // from blinking out when the form remounts on a diagram tile click. Other
  // variants (manage dispositions, testamentary) own the whole header bar.
  return (
    <>
      {headerRef.current &&
        createPortal(
          formVariant === 'editEntity' ? (
            headerControls
          ) : (
            <SectionHeaderWithAccent
              containerProps={{ pt: 1.5, pb: 1.5, pl: 2, pr: 2 }}
              header={{ heading: headerText }}
              rightHeaderContent={headerControls}
            />
          ),
          headerRef.current
        )}
      {isPreviewModalOpen && entityId && householdId && (
        <EntityPreviewModal
          entityId={entityId}
          householdId={householdId}
          onClose={closePreviewModal}
        />
      )}
    </>
  );
}
